import { flow } from '@helo/utils'
import { Bar } from '@visx/shape'

import { type ChartColorPalette } from '../../Colors'
import { Options } from '../../Options'
import type { QueryOutput } from '../../Query'
import { RenderPipeline } from '../../RenderPipeline'
import type { ChartCartesianOutput } from '../ChartCartesian'
import type {
  ChartDataOutput,
  ChartDataPoint,
  ChartDataSlice,
  ChartSeries,
  ResolvedChartSeries,
} from '../ChartData'
import { useChartInteraction } from '../ChartInteraction'
import type { ChartSeriesOutput } from '../ChartSeries'
import { useChartWrapperContext } from '../ChartWrapper/ChartWrapper'
import {
  type ChartBarLayout,
  type ChartBarOptions,
  type ChartBarOrientation,
  chartBarOptions,
} from './ChartBar.options'
import {
  ChartBarInputParts,
  ChartBarOutputParts,
  ChartBarUtils,
} from './ChartBar.parts'

export { chartBarOptions } from './ChartBar.options'
export type {
  ChartBarLayout,
  ChartBarOptions,
  ChartBarOrientation,
} from './ChartBar.options'

export type ChartBarSeries = ChartSeries & {
  color?: string
  fillColor?: string
  highlightColor?: string
}

/** Pipeline input consumed by ChartBar.setup. */
export type ChartBarInput = ChartBarOptions & {
  colorPalette?: ChartColorPalette
  _series?: Options.State[]
}

export type ResolvedChartBarSeries = ResolvedChartSeries & {
  visualization: 'bar'
  color: string
  fillColor: string
  highlightColor: string
  barOrientation: ChartBarOrientation
  barLayout: ChartBarLayout
  barSize: number
  barGap: number
  barCornerRadius: number
  seriesIndex: number
  seriesCount: number
  /** Per-point stacked baseline (0 when grouped). */
  stackOffsets: number[]
  /**
   * Original measure points when `barLayout` is `stacked100` (plot `points`
   * are percentages). Used by tooltips.
   */
  rawPoints?: ChartDataPoint[]
  /** Tooltip marker compatibility with line payload builders. */
  pointRadius: number
  lineWidth: number
}

export interface ChartBarProps {
  series: ResolvedChartSeries
  data: ChartDataSlice
}

const chartBarSetup = () =>
  RenderPipeline.createFeatureSetup<
    ChartBarInput,
    ChartSeriesOutput & Partial<ChartCartesianOutput>,
    ChartDataOutput &
      ChartSeriesOutput &
      Partial<ChartCartesianOutput> &
      Pick<QueryOutput, 'contentNode'>
  >(
    flow(
      ChartBarInputParts.setupInput(),
      ChartBarOutputParts.setBarSeries(),
      ChartBarOutputParts.setStackedCartesian(),
    ),
  )

/** Dumb mark — binds precalculated series props to VisX within wrapper scales. */
const BarMark = ({ series }: ChartBarProps) => {
  const { scales } = useChartWrapperContext()
  const { highlightedX, selectedX, setSelectedX, onCategorySelect } =
    useChartInteraction()

  if (scales == null || !ChartBarUtils.isResolvedBarSeries(series)) {
    return null
  }

  const categoryScale =
    scales.orientation === 'horizontal' ? scales.yScale : scales.xScale
  const valueScale =
    scales.orientation === 'horizontal' ? scales.xScale : scales.yScale

  return (
    <g>
      {series.points.map((point, pointIndex) => {
        const rect = ChartBarUtils.barRect({
          point,
          pointIndex,
          series,
          categoryScale,
          valueScale,
        })
        if (rect == null) return null
        const cornerRadius = ChartBarUtils.cornerRadiusForSegment(series)
        // highlightColor is a stroke accent (often white) — never use as fill.
        // Emphasize by keeping the active bar solid and dimming the rest.
        const hasEmphasis = selectedX != null || highlightedX != null
        const isActive = point.x === selectedX || point.x === highlightedX
        return (
          <Bar
            key={`${series.seriesKey}-bar-${pointIndex}`}
            x={rect.x}
            y={rect.y}
            width={rect.width}
            height={rect.height}
            fill={series.fillColor}
            fillOpacity={!hasEmphasis || isActive ? 1 : 0.35}
            rx={cornerRadius}
            ry={cornerRadius}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setSelectedX(point.x)
              onCategorySelect?.(point.x)
            }}
          />
        )
      })}
    </g>
  )
}

export const ChartBar = {
  options: chartBarOptions,
  Bar: BarMark,
  setup: chartBarSetup,
}
